/**
 * Zeilen der drei Ansichten (Board „Verlauf“): Spalten am Desktop, Karte am Handy, Details für
 * Spalte und Blatt – alles aus demselben Datensatz.
 */
import type {
  DynamicPricingDecisionHistoryItem,
  DynamicPricingHistoryResponse,
  DynamicPricingHistoryView,
} from '@/shared/api';
import { decisionCells, decisionDetail } from './historyDecision';
import type { HistoryDetail } from './historyDetailModel';
import { exposureCells, exposureDetail } from './historyExposure';
import { publicationCells, publicationDetail } from './historyPublication';
import type { Tone } from './historyText';

export type HistoryCell = string | { label: string; tone: Tone };

export interface HistoryRow {
  id: string;
  cells: HistoryCell[];
  card: { at: string; apartment: string; main: string; sub: string; status: HistoryCell };
  detail: () => HistoryDetail;
  /** Nur Entscheidungen: Herleitung und Strategie kommen aus dem Datensatz selbst. */
  decision?: DynamicPricingDecisionHistoryItem;
}

export const HISTORY_COLUMNS: Record<
  DynamicPricingHistoryView,
  { labels: string[]; grid: string }
> = {
  publications: {
    labels: ['Zeitpunkt', 'Apartment', 'Zeitraum', 'Preis · Mindestnächte', 'Zustellung'],
    grid: '104px minmax(0,1fr) 112px minmax(110px,1fr) minmax(150px,190px)',
  },
  decisions: {
    labels: [
      'Berechnet',
      'Apartment',
      'Aufenthalt',
      'Auslöser',
      'Ergebnis',
      'Preis · Mindestnächte',
    ],
    grid: '104px minmax(0,1fr) 80px minmax(0,140px) 104px minmax(150px,1.2fr)',
  },
  exposures: {
    labels: ['Gültig', 'Apartment', 'Aufenthalt', 'Art', 'Wert', 'Stand'],
    grid: '150px minmax(0,1fr) 80px 110px minmax(110px,1fr) minmax(130px,190px)',
  },
};

/** „n geladen“ – der Server nennt keine Gesamtzahl. */
export const HISTORY_NOUN: Record<DynamicPricingHistoryView, [one: string, many: string]> = {
  publications: ['Übertragung', 'Übertragungen'],
  decisions: ['Entscheidung', 'Entscheidungen'],
  exposures: ['Wert', 'Werte'],
};

export function historyRows(
  view: DynamicPricingHistoryView,
  data: DynamicPricingHistoryResponse | undefined,
): HistoryRow[] {
  if (!data) return [];
  if (view === 'publications') {
    return data.publications.items.map((item) => {
      const c = publicationCells(item);
      return {
        id: item.id,
        cells: [c.at, c.apartment, c.range, c.value, c.status],
        card: {
          at: c.at,
          apartment: c.apartment,
          main: c.value,
          sub: `Aufenthalt ${c.range}`,
          status: c.status,
        },
        detail: () => publicationDetail(item),
      };
    });
  }
  if (view === 'decisions') {
    return data.decisions.items.map((item) => {
      const c = decisionCells(item);
      return {
        id: item.id,
        cells: [c.at, c.apartment, c.day, c.trigger, c.outcome, c.value],
        card: {
          at: c.at,
          apartment: c.apartment,
          main: `${c.day} · ${c.value}`,
          sub: `Auslöser: ${c.trigger}`,
          status: c.outcome,
        },
        detail: () => decisionDetail(item),
        decision: item,
      };
    });
  }
  return data.exposures.items.map((item) => {
    const c = exposureCells(item);
    return {
      // Ein Wert kommt je Zeitabschnitt einmal, mit derselben ID – erst der Beginn macht ihn eindeutig.
      id: `${item.id}-${item.validFromUtc}`,
      cells: [c.validity, c.apartment, c.day, c.kind, c.value, c.state],
      card: {
        at: c.validity,
        apartment: c.apartment,
        main: c.value,
        sub: `${c.kind} · Aufenthalt ${c.day}`,
        status: c.state,
      },
      detail: () => exposureDetail(item),
    };
  });
}
